import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Pill, AlertTriangle, CheckCircle2, Clock, Plus, Shield, RefreshCw, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_BG = { overdue: 'bg-red-500/10 border-red-500/30', due: 'bg-[#00A6A0]/10 border-[#00A6A0]/30', available: 'bg-amber-500/10 border-amber-500/30', upcoming: 'bg-muted/30 border-border', given: 'bg-[#A8E6CF]/10 border-[#A8E6CF]/30', refused: 'bg-red-500/5 border-border' };
const STATUS_LABEL = { overdue: 'OVERDUE', due: 'DUE NOW', available: 'PRN AVAILABLE', upcoming: 'UPCOMING', given: 'GIVEN', refused: 'REFUSED' };
const STATUS_COLOR = { overdue: 'text-red-400', due: 'text-[#00A6A0]', available: 'text-amber-400', upcoming: 'text-muted-foreground', given: 'text-[#A8E6CF]', refused: 'text-red-400' };

export default function MedicationRound() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [adminDialog, setAdminDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adminForm, setAdminForm] = useState({ status: 'given', actual_time: '', notes: '', reason_not_given: '', prn_reason: '', witnessed_by_name: '', allergy_override_reason: '', duplicate_override: false, patch_site: '', insulin_units: '', blood_glucose_before: '' });
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchRound(); }, []);
  const fetchRound = async () => { try { const { data } = await axios.get(`${API}/api/clinical-compliance/mar/round`, { withCredentials: true }); setRound(data); } catch { /* ignore */ } };

  const openAdmin = (item) => {
    setSelectedItem(item);
    setAdminForm({ status: 'given', actual_time: new Date().toTimeString().slice(0, 5), notes: '', reason_not_given: '', prn_reason: '', witnessed_by_name: '', allergy_override_reason: '', duplicate_override: false, patch_site: '', insulin_units: '', blood_glucose_before: '' });
    setAdminDialog(true);
  };

  const submitAdmin = async () => {
    if (!selectedItem) return;
    try {
      const payload = { medication_id: selectedItem.medication_id, client_id: selectedItem.client_id, scheduled_time: selectedItem.scheduled_time === 'PRN' ? '' : selectedItem.scheduled_time, date: round?.date, ...adminForm };
      if (adminForm.insulin_units) payload.insulin_units = parseFloat(adminForm.insulin_units);
      if (adminForm.blood_glucose_before) payload.blood_glucose_before = parseFloat(adminForm.blood_glucose_before);
      await axios.post(`${API}/api/clinical-compliance/mar/administer`, payload, { withCredentials: true });
      toast.success(`${selectedItem.medication_name} (${selectedItem.client_name}): ${adminForm.status}`);
      setAdminDialog(false); fetchRound();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!round) return <DashboardLayout><div className="flex justify-center py-20"><div className="spinner" /></div></DashboardLayout>;

  const items = round.items || [];
  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="medication-round-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2"><Pill className="w-5 h-5 text-[#00A6A0]" />Medication Round</h1>
            <p className="text-xs text-muted-foreground">{round.date} | {round.current_time} | {round.total_items} medications across assigned clients</p>
          </div>
          <Button size="sm" variant="outline" className="border-border text-xs" onClick={fetchRound} data-testid="refresh-round"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-2">
          <button onClick={() => setFilter('overdue')} className={`p-2 rounded-lg text-center border transition-colors ${filter === 'overdue' ? 'ring-2 ring-red-500' : ''} bg-red-500/10 border-red-500/30`}>
            <p className="text-lg font-bold text-red-400">{round.overdue}</p>
            <p className="text-[10px] text-red-400">Overdue</p>
          </button>
          <button onClick={() => setFilter('due')} className={`p-2 rounded-lg text-center border transition-colors ${filter === 'due' ? 'ring-2 ring-[#00A6A0]' : ''} bg-[#00A6A0]/10 border-[#00A6A0]/30`}>
            <p className="text-lg font-bold text-[#00A6A0]">{round.due}</p>
            <p className="text-[10px] text-[#00A6A0]">Due Now</p>
          </button>
          <button onClick={() => setFilter('given')} className={`p-2 rounded-lg text-center border transition-colors ${filter === 'given' ? 'ring-2 ring-[#A8E6CF]' : ''} bg-[#A8E6CF]/10 border-[#A8E6CF]/30`}>
            <p className="text-lg font-bold text-[#A8E6CF]">{round.given}</p>
            <p className="text-[10px] text-[#A8E6CF]">Given</p>
          </button>
          <button onClick={() => setFilter('all')} className={`p-2 rounded-lg text-center border transition-colors ${filter === 'all' ? 'ring-2 ring-[#00A6A0]' : ''} bg-muted border-border`}>
            <p className="text-lg font-bold text-foreground">{round.total_items}</p>
            <p className="text-[10px] text-muted-foreground">All</p>
          </button>
        </div>

        {/* Medication items */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-8"><CheckCircle2 className="w-10 h-10 text-[#A8E6CF] mx-auto mb-2" /><p className="text-muted-foreground text-sm">No medications in this category</p></div>
          ) : filtered.map((item, i) => (
            <Card key={`${item.medication_id}-${item.scheduled_time}-${i}`} className={`border ${STATUS_BG[item.status] || 'border-border'}`} data-testid={`round-item-${i}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  {/* Status indicator */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.status === 'overdue' ? 'bg-red-500/20' : item.status === 'due' ? 'bg-[#00A6A0]/20' : item.status === 'given' ? 'bg-[#A8E6CF]/20' : 'bg-muted'}`}>
                    {item.status === 'given' ? <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" /> : item.status === 'overdue' ? <AlertTriangle className="w-5 h-5 text-red-400" /> : <Clock className="w-5 h-5 text-muted-foreground" />}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{item.medication_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${STATUS_COLOR[item.status]} bg-current/10`}>{STATUS_LABEL[item.status]}</span>
                      {item.is_controlled && <span className="text-[10px] px-1 rounded bg-red-500/10 text-red-400">CD</span>}
                      {item.is_insulin && <span className="text-[10px] px-1 rounded bg-blue-500/10 text-blue-400">INS</span>}
                      {item.is_prn && <span className="text-[10px] px-1 rounded bg-amber-500/10 text-amber-400">PRN</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                      <User className="w-3 h-3" />{item.client_name}
                      <span>{item.dose} {item.route}</span>
                      <span className="font-mono">{item.scheduled_time}</span>
                    </div>
                    {item.allergies?.length > 0 && <p className="text-[10px] text-red-400 mt-0.5">Allergies: {item.allergies.join(', ')}</p>}
                    {/* PRN Protocol inline */}
                    {item.is_prn && item.prn_protocol && (
                      <div className="mt-1 bg-amber-500/5 border border-amber-500/15 rounded p-1.5 text-[10px]" data-testid={`round-prn-protocol-${i}`}>
                        <span className="text-amber-400 font-medium">Protocol: </span>
                        <span className="text-foreground">{item.prn_indication} | Max: {item.prn_max_dose} | Interval: {item.prn_min_interval_hours}h</span>
                        {item.prn_protocol && <span className="text-muted-foreground"> | {item.prn_protocol}</span>}
                      </div>
                    )}
                  </div>

                  {/* Action */}
                  {item.status !== 'given' && item.status !== 'refused' && (
                    <Button size="sm" className={`flex-shrink-0 ${item.status === 'overdue' ? 'bg-red-500 hover:bg-red-600 text-white' : 'btn-primary'}`}
                      onClick={() => openAdmin(item)} data-testid={`admin-${i}`}>
                      {item.status === 'overdue' ? 'Urgent' : 'Give'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Quick Administration Dialog */}
      <Dialog open={adminDialog} onOpenChange={setAdminDialog}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground text-sm">{selectedItem?.medication_name}</DialogTitle>
            <DialogDescription>{selectedItem?.client_name} | {selectedItem?.dose} {selectedItem?.route} @ {selectedItem?.scheduled_time}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <select value={adminForm.status} onChange={e => setAdminForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-2 py-1.5 text-foreground text-xs">
              <option value="given">Given</option><option value="refused">Refused</option><option value="withheld">Withheld</option><option value="omitted">Omitted</option><option value="error">Error</option>
            </select>
            {adminForm.status !== 'given' && <Input value={adminForm.reason_not_given} onChange={e => setAdminForm(f => ({ ...f, reason_not_given: e.target.value }))} placeholder="Reason" className="bg-muted border-border text-foreground text-xs" />}
            {selectedItem?.is_prn && <Input value={adminForm.prn_reason} onChange={e => setAdminForm(f => ({ ...f, prn_reason: e.target.value }))} placeholder="PRN reason" className="bg-muted border-border text-foreground text-xs" />}
            {selectedItem?.is_controlled && <Input value={adminForm.witnessed_by_name} onChange={e => setAdminForm(f => ({ ...f, witnessed_by_name: e.target.value }))} placeholder="Witness name (CD)" className="bg-muted border-border text-foreground text-xs" />}
            {selectedItem?.is_insulin && (
              <div className="grid grid-cols-2 gap-1">
                <Input value={adminForm.insulin_units} onChange={e => setAdminForm(f => ({ ...f, insulin_units: e.target.value }))} placeholder="Units" className="bg-muted border-border text-foreground text-xs" type="number" />
                <Input value={adminForm.blood_glucose_before} onChange={e => setAdminForm(f => ({ ...f, blood_glucose_before: e.target.value }))} placeholder="BG mmol/L" className="bg-muted border-border text-foreground text-xs" type="number" step="0.1" />
              </div>
            )}
            <Input value={adminForm.notes} onChange={e => setAdminForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="bg-muted border-border text-foreground text-xs" />
            <Button className="w-full btn-primary" onClick={submitAdmin} data-testid="round-confirm-btn">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
